import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import type { LoopCandidate } from '../audio/loops';

interface Props {
  peaks: Float32Array;
  duration: number;
  bpm: number;
  offset: number;
  beatsPerBar: number;
  /** Selected region, seconds. */
  selection: { start: number; end: number };
  candidates: LoopCandidate[];
  /** Snap resolution in bars, drawn as ticks when finer than a bar. */
  snapBars: number;
  /** 0..1 within the selection while previewing, or null. */
  playhead: number | null;
  height?: number;
  /** Requested loop start, in seconds. The parent snaps it. */
  onScrub: (seconds: number) => void;
}

/**
 * Whole-track waveform with the bar grid over it.
 *
 * Bar lines thin out automatically as the track gets longer — drawing four
 * hundred of them across 1200 pixels is just a grey wash — so you always get
 * lines you can actually count against.
 */
export function TrackOverview({
  peaks,
  duration,
  bpm,
  offset,
  beatsPerBar,
  selection,
  candidates,
  snapBars,
  playhead,
  height = 150,
  onScrub,
}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dragging = useRef(false);
  /** Seconds between the selection start and where the pointer grabbed it. */
  const grab = useRef(0);
  const [overSelection, setOverSelection] = useState(false);
  const [grabbing, setGrabbing] = useState(false);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const xOf = (t: number) => (t / Math.max(0.001, duration)) * w;

    ctx.fillStyle = '#0b0e11';
    ctx.fillRect(0, 0, w, h);

    // Selection band sits behind the waveform so it reads as highlight.
    const sx = xOf(selection.start);
    const ex = xOf(selection.end);
    ctx.fillStyle = 'rgba(6, 214, 160, 0.16)';
    ctx.fillRect(sx, 0, Math.max(2, ex - sx), h);

    const buckets = peaks.length / 2;
    const mid = h / 2;
    for (let x = 0; x < w; x++) {
      const b0 = Math.floor((x / w) * buckets);
      const b1 = Math.max(b0 + 1, Math.floor(((x + 1) / w) * buckets));
      let lo = 0;
      let hi = 0;
      for (let b = b0; b < b1 && b < buckets; b++) {
        lo = Math.min(lo, peaks[b * 2]);
        hi = Math.max(hi, peaks[b * 2 + 1]);
      }
      const inSel = x >= sx && x <= ex;
      ctx.fillStyle = inSel ? '#9fb4c2' : '#4c5966';
      const y0 = mid - hi * mid * 0.9;
      const y1 = mid - lo * mid * 0.9;
      ctx.fillRect(x, y0, 1, Math.max(1, y1 - y0));
    }

    // Bar grid, thinned so lines stay at least 9px apart.
    const barSec = (60 / bpm) * beatsPerBar;
    if (barSec > 0) {
      const pxPerBar = xOf(barSec) - xOf(0);
      const stride = Math.max(1, Math.pow(2, Math.ceil(Math.log2(16 / Math.max(0.01, pxPerBar)))));
      ctx.font = '9px "DM Mono", monospace';
      for (let bar = 0; ; bar += stride) {
        const t = offset + bar * barSec;
        if (t > duration) break;
        const x = xOf(t);
        const major = bar % (stride * 4) === 0;
        ctx.fillStyle = major ? 'rgba(230,235,239,0.3)' : 'rgba(230,235,239,0.12)';
        ctx.fillRect(x, major ? 0 : h - 14, 1, major ? h : 14);
        if (major && pxPerBar * stride > 40) {
          ctx.fillStyle = 'rgba(230,235,239,0.4)';
          ctx.fillText(String(bar + 1), x + 3, 10);
        }
      }
    }

    // Snap ticks, so it's visible where a drag will land.
    if (barSec > 0 && snapBars < 1) {
      const snapSec = barSec * snapBars;
      const pxPerSnap = xOf(snapSec) - xOf(0);
      if (pxPerSnap >= 5) {
        ctx.fillStyle = 'rgba(6, 214, 160, 0.28)';
        for (let t = offset; t <= duration; t += snapSec) ctx.fillRect(xOf(t), h - 9, 1, 4);
      }
    }

    // Candidate markers along the bottom.
    for (const c of candidates) {
      const cx = xOf(c.startSec);
      const cw = Math.max(3, xOf(c.endSec) - cx);
      ctx.fillStyle = `rgba(255, 209, 102, ${0.25 + c.score * 0.5})`;
      ctx.fillRect(cx, h - 5, cw, 5);
    }

    // Selection edges last, so they sit above everything.
    ctx.fillStyle = '#8b5cf6';
    ctx.fillRect(sx - 1, 0, 2, h);
    ctx.fillRect(ex - 1, 0, 2, h);

    if (playhead !== null) {
      const px = sx + (ex - sx) * playhead;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px - 0.5, 0, 1.5, h);
    }
  }, [peaks, duration, bpm, offset, beatsPerBar, selection, candidates, snapBars, playhead, height]);

  const timeAt = (clientX: number, el: HTMLCanvasElement) => {
    const rect = el.getBoundingClientRect();
    return ((clientX - rect.left) / rect.width) * duration;
  };

  return (
    <canvas
      ref={ref}
      style={{ height }}
      className={clsx(
        'w-full touch-none rounded-lg border border-ink-700',
        grabbing ? 'cursor-grabbing' : overSelection ? 'cursor-grab' : 'cursor-crosshair',
      )}
      onPointerDown={(e) => {
        const t = timeAt(e.clientX, e.currentTarget);
        e.currentTarget.setPointerCapture(e.pointerId);
        // Grabbing inside the region moves it and keeps the point you grabbed;
        // clicking outside drops the loop start where you clicked.
        grab.current = t >= selection.start && t <= selection.end ? t - selection.start : 0;
        dragging.current = true;
        setGrabbing(true);
        onScrub(t - grab.current);
      }}
      onPointerMove={(e) => {
        const t = timeAt(e.clientX, e.currentTarget);
        if (dragging.current) onScrub(t - grab.current);
        else setOverSelection(t >= selection.start && t <= selection.end);
      }}
      onPointerUp={() => {
        dragging.current = false;
        setGrabbing(false);
      }}
      onPointerCancel={() => {
        dragging.current = false;
        setGrabbing(false);
      }}
      onPointerLeave={() => setOverSelection(false)}
    />
  );
}
